import {extend, localize, setInteractionMode} from 'vee-validate';
import {required, email, min, length} from 'vee-validate/dist/rules';
import _ from 'lodash';
import store from "@/store";
import i18n from '@/i18n';
import moment from 'moment'
import log from 'loglevel'

const customRegexValidationRules = []

setInteractionMode('custom', ({errors}) => {
  if (errors.length) {
    return {
      on: ['input', 'change']
    };
  }

  return {
    on: ['change', 'blur', 'input']
  };
});

function getValidMomentDate(date) {
  let momentDate = null

  if (moment(date, 'YYYY-MM-DD', true).isValid()) {
    momentDate = moment(date, 'YYYY-MM-DD')
  } else if (moment(date, 'DD.MM.YYYY', true).isValid()) {
    momentDate = moment(date, 'DD.MM.YYYY')
  }

  return momentDate
}

function addValidationRules() {
  extend('required', required);
  extend('email', email);
  extend('min', min);
  extend('length', length);

  extend('compareFields', {
    params: ['target'],
    validate(value, {target}) {
      return value === target;
    }
  });

  extend('requiredGender', {
    validate(value) {
      return typeof value === 'string';
    },
    computesRequired: true
  });

  extend('requiredSelect', {
    validate(value) {
      if (Array.isArray(value)) return value.length > 0;
      return typeof value === 'string' && value != null && value != '' && value != 'placeholder';
    },
    computesRequired: true
  });

  extend('requiredSwitch', {
    validate(value) {
      return value === true
    }
  })

  extend('isNumber', {
    validate(value) {
      return /^[0-9]+$/g.test(value);
    }
  })

  extend('phoneNumber', {
    validate(value) {
      return /^[0-9()+-\s/.]*$/g.test(value);
    }
  });

  extend('cityCode', {
    validate(value) {
      return /^[0-9]{4,5}$/g.test(value);
    }
  })

  extend('emailOrPhone', {
    validate(value) {
      return /^[0-9()+-\s/.]*$/g.test(value) || email.validate(value)
    }
  })

  extend('customRegex', {
    validate(value, {validationSchema}) {
      if (_.isFunction(validationSchema.validator) && validationSchema.validator(value)) {
        return true
      }

      return validationSchema.errorMessage
    },
    params: ['validationSchema']
  })

  extend('minimumAge', {
    validate(value, {validationSchema}) {
      const birthday = getValidMomentDate(value)

      if (_.isInteger(validationSchema.minAllowedAge) && birthday) {
        if (moment().diff(birthday, 'years') >= validationSchema.minAllowedAge) {
          return true
        }

        return validationSchema.errorMessage
      }

      return validationSchema.errorMessage
    },
    params: ['validationSchema']
  })
}

function addCustomRegexValidationRule(config) {
  const validator = _.get(store.getters[config.path], [config.name], {})

  if (_.isEmpty(validator) || !_.isObject(validator)) {
    return
  }

  const {regex = '.*'} = validator
  const regExpression = new RegExp(regex)

  customRegexValidationRules.push(config.name)

  extend(config.name, {
    validate(value) {
      return regExpression.test(value)
    }
  })
}

function isCustomRegexValidationRuleAvailable(name) {
  return _.includes(customRegexValidationRules, name)
}

function addValidationTranslations(currentLocale) {
  localize(currentLocale)

  const availableMessages = i18n.messages
  const localeObject = {}

  Object.keys(availableMessages).forEach((key) => {
    localeObject[`${key}`] = {
      messages: _.get(availableMessages[key], 'validations.messages', null),
      names: _.get(availableMessages[key], 'validations.names', null),
      fields: {
        emailRepeat: {
          compareFields: _.get(availableMessages[key], 'registerScreen.emailRepeatNotEqual', '')
        },
        passwordNewRepeat: {
          compareFields: _.get(availableMessages[key], 'profile.passwordRepeatNotEqual', '')
        },
        tos: {
          required: _.get(availableMessages[key], 'auth.tosNotAcceptedAlertText', '')
        }
      }
    }
  })

  localize(localeObject)
}

function runtimeConditionsAreMet(formData, conditions) {
  if (_.isEmpty(conditions) || _.isEmpty(formData)) {
    return true
  }

  const conditionsAreMet = _.some(
    conditions,
    (condition) => {
      const keyPath = _.get(condition, 'key', '')
      const allowedValues = _.get(condition, 'allowed_values', [])

      if (!keyPath || _.isEmpty(allowedValues)) {
        return false
      }

      const keyPathSegments = keyPath.split('.')
      const lookupKey = keyPathSegments[keyPathSegments.length - 1]
      let value = ''

      if (keyPathSegments[0] === 'custom_user_field') {
        const lookupFields = _.get(
          formData,
          'custom_fields',
          []
        )

        const field = lookupFields.find((field) => {
          return field.key === lookupKey
        })

        if (field) {
          value = field.value
        }
      }

      const valueFound = _.some(
        allowedValues,
        (allowedValue) => {
          return allowedValue === value
        })

      return valueFound
  })

  return conditionsAreMet
}

function getFieldCustomRegexValidation(validators) {
  if (!validators) {
    return null
  }

  const {
    enabled,
    regex,
    error_message: errorMessage,
  } = validators

  if (_.has(validators, 'enabled') ? !enabled : true || !regex || !errorMessage) {
    return null
  }

  try {
    const regexExp = new RegExp(regex)
    const regexValidator = (value) => regexExp.test(value)

    return {
      validator: regexValidator,
      errorMessage,
    }
  } catch (error) {
    log.warn('Invalid regular expression')
    return null
  }
}

function getFieldRules(field) {
  const {required, rules, regexValidation, validators} = field

  if (validators && validators.enabled && validators.minimum_age && validators.error_message) {
    return {
      required,
      minimumAge: {
        minAllowedAge: validators.minimum_age,
        errorMessage: validators.error_message
      }
    }
  }

  if (regexValidation) {
    return {
      required,
      customRegex: regexValidation
    }
  }

  const fieldRequired = required ? 'required' : ''
  const fieldRules = rules && rules !== 'undefined' ? rules : ''

  return [fieldRequired, fieldRules].filter(Boolean).toString().replace(/,/g, '|')
}

function getMaxDateField(field) {
  const date = new Date()

  if (field.key === 'birthday') {
    const minAge = _.get(field, 'validators.minimum_age', '')

    if (minAge) {
      date.setFullYear(date.getFullYear() - minAge)
      return date.toISOString().substr(0, 10)
    }
  }

  if (field.pastOnly) {
    return date.toISOString().substr(0, 10)
  }

  return ''
}

export {
  addValidationRules,
  addCustomRegexValidationRule,
  addValidationTranslations,
  runtimeConditionsAreMet,
  isCustomRegexValidationRuleAvailable,
  getFieldCustomRegexValidation,
  getFieldRules,
  getMaxDateField,
}
